import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ShieldCheck, Star, Users } from 'lucide-react';
import { staggerContainer, heroWord, fadeUp } from '../../animations/variants';

const headline = ['Quality', 'Medical', 'Supplies,', 'Trusted', 'Care.'];

const stats = [
  { icon: Star, value: '5+ Brands', label: 'Compression experts' },
  { icon: ShieldCheck, value: 'Medical Grade', label: 'Certified products' },
  { icon: Users, value: 'Personal', label: 'Service & support' },
];

export function HeroSection() {
  return (
    <section
      id="hero"
      className="relative flex flex-col items-center overflow-hidden bg-ndms-navy"
    >
      {/* Animated background */}
      <div className="absolute inset-0 hero-pattern opacity-100" />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.3) 0%, transparent 60%)',
            'radial-gradient(ellipse at 80% 50%, rgba(8,145,178,0.3) 0%, transparent 60%)',
            'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.3) 0%, transparent 60%)',
          ],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 container-max section-padding text-center flex flex-col items-center pt-8 pb-16">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5"
        >
          <span className="w-2 h-2 bg-ndms-teal-light rounded-full animate-pulse" />
          <span className="text-sm font-medium text-white/90">Northdale Medical Supplies</span>
        </motion.div>

        {/* Headline */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="mb-6"
          style={{ perspective: 800 }}
        >
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-display font-bold text-white leading-tight tracking-tight">
            {headline.map((word, i) => (
              <React.Fragment key={i}>
                <motion.span
                  variants={heroWord}
                  className="inline-block mr-3 sm:mr-4"
                  style={{ display: 'inline-block' }}
                >
                  {word}
                </motion.span>
                {i === 2 && <br className="hidden sm:block" />}
              </React.Fragment>
            ))}
          </h1>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.8 }}
          className="text-lg sm:text-xl text-white/75 max-w-2xl mb-10 leading-relaxed"
        >
          Compression stockings, back braces, knee braces, and more — expertly selected, in-stock, and fulfilled personally by our team.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row items-center gap-4 mb-16"
        >
          <motion.a
            href="#compression"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="px-8 py-4 bg-white text-ndms-navy font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all text-base"
          >
            Shop Compression Stockings
          </motion.a>
          <motion.a
            href="#braces"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-2xl hover:bg-white/20 transition-all text-base"
          >
            Browse All Braces
          </motion.a>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          transition={{ delayChildren: 1.1, staggerChildren: 0.12 }}
          className="flex flex-col sm:flex-row items-center gap-6 sm:gap-12 mb-20 sm:mb-0"
        >
          {stats.map(({ icon: Icon, value, label }) => (
            <motion.div
              key={value}
              variants={fadeUp}
              className="flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon size={18} className="text-ndms-teal-light" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">{value}</div>
                <div className="text-white/60 text-xs">{label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#compression"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 hover:text-white/80 transition-colors"
      >
        <span className="text-xs font-medium tracking-widest uppercase">Explore</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown size={20} />
        </motion.div>
      </motion.a>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 80L1440 80L1440 40C1200 80 720 0 0 40L0 80Z" fill="white" />
        </svg>
      </div>
    </section>
  );
}
